import React, { Suspense } from "react";
import AdminLayout from "../../components/layouts/AdminLayout";
import ProtectedRoute from "../utils/ProtectedRoute";
import RequireRole from "../utils/RequireRole";

// Lightweight fallback while a lazy-loaded page chunk is fetched.
// Renders inside AdminLayout so the sidebar + topbar stay visible
// during navigation and only the content area flashes.
const RouteFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[40vh]">
    <div className="flex flex-col items-center gap-2">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
    </div>
  </div>
);

// Helper for admin routes with role protection
export const adminElement = (component: React.ReactElement) => (
  <ProtectedRoute>
    <RequireRole allowedRoles={["admin"]}>
      <AdminLayout>
        <Suspense fallback={<RouteFallback />}>{component}</Suspense>
      </AdminLayout>
    </RequireRole>
  </ProtectedRoute>
);

// Helper for protected routes that don't require a specific role —
// still must be authenticated. Earlier this skipped ProtectedRoute,
// which meant any caller flagged onlyProtected (Brand/Category/Product
// CRUD) rendered without an auth check. Wrap with ProtectedRoute so
// unauthenticated visitors get bounced to /login.
export const protectedElement = (component: React.ReactElement) => (
  <ProtectedRoute>
    <AdminLayout>
      <Suspense fallback={<RouteFallback />}>{component}</Suspense>
    </AdminLayout>
  </ProtectedRoute>
);

// Helper for printable routes — auth-gated like a normal admin route
// but without the AdminLayout chrome (sidebar/topbar/etc.) so the page
// is clean for window.print(). Used by shipping label / packing slip.
export const printableElement = (component: React.ReactElement) => (
  <ProtectedRoute>
    <RequireRole allowedRoles={["admin"]}>
      <Suspense fallback={<RouteFallback />}>{component}</Suspense>
    </RequireRole>
  </ProtectedRoute>
);